import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { baseUrl } from 'urls'
import { cn } from '@/features/utils'
import { changeApplication, signOut, updateQueryJson } from 'actions/rootActions'
import { redirectHomepage } from 'loginControl'
import { unMimicUser, getMimicStatus } from 'actions/mimicActions'
import { getMimicTokenFromCookie, redirectAfterUnmimic } from 'modules/mimicToken'
import { getAxiosDefaultConfig } from '../../modules/utils'
import {
  fetchOpenAIChatHistory,
  fetchMoreOpenAIChatHistory,
  deleteOpenAIChatHistory,
  deleteAllOpenAIChatHistory,
  updateOpenAIChatPrivacy,
  togglePinOpenAIChatHistory,
  selectOpenAIChatHistory,
  selectOpenAIChatHistoryStatus,
  selectOpenAIChatHistoryHasMore,
  selectOpenAIChatHistoryLoadingMore,
  selectOpenAIChatHistoryOffset,
  selectActiveChatIds,
  setSelectedTaskId,
  selectToolAccessSummaryByChatId,
} from '../../features/openai/openaiSlice'
import { getChatHistoryListPreview } from '../../features/openai/chatHistoryPreview'
import { toast } from 'react-toastify'
import { fetchProfile, selectProfile, selectProfileStatus } from '../../applications/Settings/settingsSlice'
import { pickDisplayProfileImageUrl } from '../../modules/profileImageUrl'
import {
  teamNavLabel,
  teamPageAccessAllowed,
} from '../../applications/Settings/views/SharedAccounts/teamPagePermissions'
import { useCanShip } from '../canShip'
import { PERMISSIONS } from '../Router.permissionRoute'
import { useFeatureFlag, FEATURE_FLAGS } from '../../modules/featureFlags'
import { hasConnectedStore } from '../../modules/storeTools'
import _ from 'lodash'
import {
  LayoutDashboard,
  TrendingUp,
  FileText,
  Link2,
  Sparkles,
  Truck,
  Package,
  ChevronDown,
  ChevronRight,
  Settings,
  HelpCircle,
  LogOut,
  Users,
  BarChart3,
  RefreshCw,
  Shield,
  Ban,
  CreditCard,
  Coins,
  Eye,
  ShoppingCart,
  UserCircle,
  Map,
  Megaphone,
  ShoppingBag,
  MousePointer,
  Layers,
  Activity,
  Target,
  Wrench,
  SlidersHorizontal,
  Puzzle,
  History,
  ClipboardList,
  Plug,
  ListChecks,
} from 'lucide-react'

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarFooter,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  useSidebar,
} from '@/features/shadcn/ui/sidebar'
import { Popover, PopoverTrigger, PopoverContent } from '@/features/shadcn/ui/popover'

import Logo from '../../assets/images/payhelm-logo-blue.png'
import IconLogo from '../../assets/images/PayHelm_Icon_PNG.png'
import PromoCard from './components/PromoCard'
import SidebarDrillDownPanel from './components/SidebarDrillDownPanel'
import BackfillBanner from 'sharedComponents/BackfillBanner'
import OrgSwitcherMenu from './components/OrgSwitcherMenu'
import CreateOrganizationWizard from './components/CreateOrganizationWizard'
import {
  CREATE_ORG_WIZARD_RESUME_KEY,
  fetchOrganizations,
  provisionOrganizationTenancy,
  selectHomeUserEmail,
  selectIsWorkspaceContext,
  selectOrganizations,
  selectOrganizationsStatus,
} from '../../features/organizations/organizationsSlice'
import {
  isOrgIdLabel,
  isSyntheticWorkspaceEmail,
  resolveAccountDisplayLabel,
  resolvePersonalAccountEmail,
} from '../../features/organizations/accountDisplay'
import { customTabView } from '../../applications/Dashboard/dashboardTabRouting'
import { sortedDashboardTabs, withDisambiguatedTabTitles } from '../../modules/widgetSpec'

// Dashboard submenu items
const dashboardItems = [
  { title: 'Overview', url: '/dashboard?view=overview', icon: Eye },
  { title: 'Orders', url: '/dashboard?view=orders', icon: ShoppingCart },
  { title: 'Products', url: '/dashboard?view=products', icon: Package },
  { title: 'Customers', url: '/dashboard?view=customer', icon: UserCircle },
  { title: 'Marketing', url: '/dashboard?view=marketing', icon: Megaphone },
  { title: 'Map', url: '/dashboard?view=map', icon: Map },
]

// Traffic submenu items
const trafficItems = [
  { title: 'Overview', url: '/traffic?view=metrics', icon: Eye },
  { title: 'Ads', url: '/traffic?view=ads', icon: Target },
  { title: 'Trends', url: '/traffic?view=overview', icon: Activity },
  { title: 'Orders', url: '/traffic?view=orders', icon: ShoppingCart },
  { title: 'Checkouts', url: '/traffic?view=checkouts', icon: ShoppingBag },
  { title: 'Carts', url: '/traffic?view=carts', icon: Layers },
  { title: 'Visitors', url: '/traffic?view=visitors', icon: MousePointer },
  { title: 'Sessions', url: '/traffic?view=sessions', icon: BarChart3 },
]

// AI Chat submenu items (static items)
const aiChatStaticItems = [
  { title: 'New Chat', url: '/ai', icon: Sparkles, action: 'new-chat' },
  { title: 'Task Library', url: '/task-library', icon: ListChecks, action: 'task-library' },
  { title: 'Custom Tools', url: '/ai?view=custom-tools', icon: Wrench, action: 'custom-tools' },
  { title: 'Tool Access', url: '/ai?view=tool-access', icon: History, action: 'tool-access' },
]

const planViewsHiddenForShopify = new Set(['add-ons', 'billing', 'invoices'])

const planDrillNavItems = [
  { viewKey: 'options', title: 'Options', url: '/plan?view=options', icon: SlidersHorizontal },
  { viewKey: 'add-ons', title: 'Add-Ons', url: '/plan?view=add-ons', icon: Puzzle },
  { viewKey: 'billing', title: 'Billing', url: '/plan?view=billing', icon: CreditCard },
  { viewKey: 'ai-credits', title: 'AI Credits', url: '/plan?view=ai-credits', icon: Coins },
  { viewKey: 'invoices', title: 'Invoices', url: '/plan?view=invoices', icon: FileText },
  { viewKey: 'paymentHistory', title: 'Payment History', url: '/plan?view=paymentHistory', icon: History },
] as const

function visiblePlanNavItems(billingChannel: string | null | undefined) {
  if (billingChannel !== 'shopify') return [...planDrillNavItems]
  return planDrillNavItems.filter((item) => !planViewsHiddenForShopify.has(item.viewKey))
}

// Main navigation items (excluding Dashboard, Traffic, AI Chat, and Shipping which have submenus)
const mainNavItems = [
  { title: 'Reports', url: '/reports', icon: FileText },
  // { title: 'Email Campaigns', url: '/emailcampaigns', icon: Mail },
  { title: 'Integrations', url: '/integrations', icon: Link2 },
  { title: 'Inventory', url: '/inventory', icon: Package },
]

type AccountNavItem = {
  title: string
  url: string
  icon: React.ComponentType<{ className?: string }>
  external?: boolean
  /** When set, item is hidden if team page permission is off for this viewer. */
  permissionKey?: string
}

// Profile menu (popover / mobile collapsible): My Team, Preferences, Support
const accountNavItems: AccountNavItem[] = [
  { title: 'My Team', url: '/settings?view=shared_accounts', icon: Users, permissionKey: 'shared_accounts' },
  { title: 'Preferences', url: '/settings?view=preferences', icon: Settings, permissionKey: 'preferences' },
  { title: 'Support', url: 'https://www.payhelm.com/help/', icon: HelpCircle, external: true },
]

type SidebarOpenSectionsState = {
  dashboard: boolean
  traffic: boolean
  ai: boolean
  shipping: boolean
}

/** All nav sections closed — spread then set one `true` to avoid duplicate object keys. */
const SIDEBAR_SECTIONS_ALL_CLOSED: SidebarOpenSectionsState = {
  dashboard: false,
  traffic: false,
  ai: false,
  shipping: false,
}

// Sidebar promo card (placeholders)
const sidebarPromoCard = {
  title: 'Unlock AI-Powered Analytics',
  description: 'Get deeper insights into your business data with PayHelm AI — smarter reports, trends, and forecasts on demand.',
  buttonLabel: 'Add AI to My Plan',
  imageUrl: 'https://placehold.co/320x180/png?text=Promo+Image',
}

interface User {
  id?: string | number
  email?: string
  name?: string
  is_mimic?: boolean
  organization_name?: string | null
}

type SidebarViewId = 'main' | 'ai' | 'plan' | 'my-team'

interface AppSidebarProps {
  currentApp: string
  isOwner: boolean
  mimicStatus: boolean
  subscriptionActive: boolean
  upgradeDismissed: boolean
  hasAccounts: boolean
  isLite: boolean
  accessPermissions: Record<string, boolean>
  user: User
  profile: { displayName?: string | null; profileImageUrl?: string | null } | null
  profileStatus: string
  changeApplication: (currentApp: string, nextApp: string) => void
  signOut: () => Promise<any>
  unMimicUser: (token: string) => Promise<void>
  getMimicStatus: (token: string) => Promise<void>
  updateQueryJson: () => void
  billingChannel: string | null
  /** Store-less WooCommerce (no connected store) → hide analytics nav, keep AI. See #730. */
  siteToolsOnly: boolean
}

const AppSidebarComponent: React.FC<AppSidebarProps> = ({
  currentApp,
  isOwner,
  mimicStatus,
  subscriptionActive,
  upgradeDismissed,
  hasAccounts,
  isLite,
  accessPermissions,
  user,
  profile,
  profileStatus,
  changeApplication,
  signOut: signOutAction,
  unMimicUser: unMimicUserAction,
  getMimicStatus: getMimicStatusAction,
  updateQueryJson: updateQueryJsonAction,
  billingChannel,
  siteToolsOnly,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const organizations = useSelector(selectOrganizations)
  const organizationsStatus = useSelector(selectOrganizationsStatus)
  const isWorkspaceContext = useSelector(selectIsWorkspaceContext)
  const homeUserEmail = useSelector(selectHomeUserEmail)
  const customDashboardTabsConfig = useSelector(
    (state: any) => state.root?.user?.dashboardconfig?.tabs
  )
  const customDashboardTabs = useMemo(
    () => withDisambiguatedTabTitles(sortedDashboardTabs(customDashboardTabsConfig)),
    [customDashboardTabsConfig]
  )
  const visibleDashboardItems = useMemo(
    () => [
      ...dashboardItems,
      ...customDashboardTabs.map((tab) => ({
        title: tab.displayTitle,
        url: `/dashboard?view=${customTabView(tab.id)}`,
        icon: LayoutDashboard,
      })),
    ],
    [customDashboardTabs]
  )
  const status = !!(mimicStatus || user?.is_mimic)
  const { state: sidebarState, isMobile, setOpenMobile, setOpen } = useSidebar()
  /** Icon-only logo is for the desktop collapsed rail only; mobile sheet always uses the wordmark. */
  const sidebarShowsIconLogo = !isMobile && sidebarState === 'collapsed'
  const [navStack, setNavStack] = useState<SidebarViewId[]>(['main'])
  const [accountPopoverOpen, setAccountPopoverOpen] = useState(false)
  const [createOrgWizardOpen, setCreateOrgWizardOpen] = useState(false)
  const [sidebarAvatarError, setSidebarAvatarError] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem(CREATE_ORG_WIZARD_RESUME_KEY)) {
      setCreateOrgWizardOpen(true)
    }
  }, [])

  const [openSections, setOpenSections] = useState<SidebarOpenSectionsState>(() => ({
    ...SIDEBAR_SECTIONS_ALL_CLOSED,
  }))

  // When opening a submenu, close the others (accordion behavior)
  const setSectionOpen = (section: keyof typeof openSections) => (open: boolean) => {
    setOpenSections((prev) => {
      if (open) {
        return { ...SIDEBAR_SECTIONS_ALL_CLOSED, [section]: true }
      }
      return { ...prev, [section]: false }
    })
  }

  // Fetch AI chat history
  const openAIChatHistory = useSelector(selectOpenAIChatHistory)
  const openAIChatHistoryStatus = useSelector(selectOpenAIChatHistoryStatus)
  // message_ids of conversations with an in-progress turn (shared loading state).
  const activeChatIds = useSelector(selectActiveChatIds)
  const chatHistoryHasMore = useSelector(selectOpenAIChatHistoryHasMore)
  const chatHistoryLoadingMore = useSelector(selectOpenAIChatHistoryLoadingMore)
  const chatHistoryOffset = useSelector(selectOpenAIChatHistoryOffset)
  const toolAccessSummary = useSelector(selectToolAccessSummaryByChatId)
  const [chatSearchInput, setChatSearchInput] = useState('')
  const [chatSearchItems, setChatSearchItems] = useState<any[]>([])
  const [chatSearchLoading, setChatSearchLoading] = useState(false)
  const chatSearchAbortRef = React.useRef<AbortController | null>(null)

  const trimmedChatSearch = chatSearchInput.trim()
  const isSidebarSearching = trimmedChatSearch.length > 0

  const runSidebarSearch = useCallback(async (query: string) => {
    if (!user?.email || !query) return
    const controller = new AbortController()
    chatSearchAbortRef.current?.abort()
    chatSearchAbortRef.current = controller
    setChatSearchLoading(true)
    try {
      const params = new URLSearchParams({ q: query, limit: '20', offset: '0' })
      const res = await axios.get(
        `${baseUrl}/openai/chat/history/${encodeURIComponent(user.email)}/search?${params.toString()}`,
        { ...getAxiosDefaultConfig(), signal: controller.signal }
      )
      if (controller.signal.aborted) return
      setChatSearchItems(Array.isArray(res?.data?.items) ? res.data.items : [])
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return
    } finally {
      setChatSearchLoading(false)
    }
  }, [user?.email])

  useEffect(() => {
    if (!trimmedChatSearch) {
      chatSearchAbortRef.current?.abort()
      setChatSearchItems([])
      return
    }
    const id = window.setTimeout(() => void runSidebarSearch(trimmedChatSearch), 250)
    return () => {
      window.clearTimeout(id)
      chatSearchAbortRef.current?.abort()
    }
  }, [trimmedChatSearch, runSidebarSearch])

  useEffect(() => {
    if (openAIChatHistoryStatus === 'idle' && user?.email) {
      dispatch(fetchOpenAIChatHistory({ app_email: user.email }))
    }
  }, [user, openAIChatHistoryStatus, dispatch])

  const handleLoadMoreHistory = () => {
    if (user?.email && chatHistoryHasMore && !chatHistoryLoadingMore) {
      dispatch(fetchMoreOpenAIChatHistory({ app_email: user.email, offset: chatHistoryOffset }))
    }
  }

  useEffect(() => {
    if (organizationsStatus === 'idle') {
      dispatch(fetchOrganizations() as never)
    }
  }, [organizationsStatus, dispatch])

  useEffect(() => {
    const token = getMimicTokenFromCookie()
    if (token) {
      getMimicStatusAction(token)
    }
  }, [getMimicStatusAction])

  useEffect(() => {
    if (user?.email) {
      dispatch(fetchProfile())
    }
  }, [user?.email, dispatch])

  const activeOrg = organizations.find((org) => org.is_active)
  const inOrgContext = isWorkspaceContext && !!activeOrg
  const planNavAllowed = teamPageAccessAllowed(accessPermissions, 'plan')
  // Release master switches for nav visibility (layer 1). RBAC still filters
  // each item (layer 2). Hidden nav is convenience only — routes + API are
  // independently gated.
  const shippingFeatureEnabled = useFeatureFlag(FEATURE_FLAGS.SHIPPING)
  const canShippingSettings = useCanShip(PERMISSIONS.SHIPPING_SETTINGS)
  const shippingItems = useMemo(
    () => {
      const items = [
        { title: 'Orders', url: '/shipping/orders', icon: Package },
        { title: 'Shipments', url: '/shipping/shipments', icon: Truck },
        { title: 'Insured', url: '/shipping/shipments?view=insured', icon: Shield },
        { title: 'Claims', url: '/shipping/shipments?view=claims', icon: ClipboardList },
      ]
      if (canShippingSettings) {
        items.push({ title: 'Settings', url: '/shipping/settings', icon: Settings })
      }
      return items
    },
    [canShippingSettings],
  )
  const visibleAccountNavItems = accountNavItems
    .filter(
      (item) => !item.permissionKey || teamPageAccessAllowed(accessPermissions, item.permissionKey)
    )
    .map((item) =>
      item.permissionKey === 'shared_accounts'
        ? { ...item, title: teamNavLabel(inOrgContext) }
        : item
    )

  // Store-less (site-tools-only) accounts have no order/product data, so the
  // analytics nav (Reports, Inventory) would dead-end. Keep Integrations so they
  // can connect a store later. See #730.
  const visibleMainNavItems = siteToolsOnly
    ? mainNavItems.filter((item) => item.url === '/integrations')
    : mainNavItems

  const personalEmail = resolvePersonalAccountEmail(user, homeUserEmail)
  const accountLabelEmail = isWorkspaceContext ? personalEmail : user?.email || ''
  const displayLabel = resolveAccountDisplayLabel({
    profileDisplayName: profile?.displayName,
    userName: user?.name,
    personalEmail: accountLabelEmail,
  })
  const displayInitial = (
    [profile?.displayName, user?.name].find((v) => v && !isOrgIdLabel(v)) ||
    accountLabelEmail ||
    'U'
  )
    .trim()
    .charAt(0)
    .toUpperCase()
  const resolvedProfileImageUrl = pickDisplayProfileImageUrl(
    profile?.profileImageUrl,
    activeOrg?.logo_url
  )
  const profileImageUrl =
    resolvedProfileImageUrl && !sidebarAvatarError ? resolvedProfileImageUrl : null
  // Show the org name whenever an active org is known; if the account label would
  // otherwise be a raw org id (workspace session before orgs/profile resolve), the
  // org name is the best readable label.
  const footerOrgName =
    activeOrg && (isWorkspaceContext || isOrgIdLabel(user?.name))
      ? activeOrg.display_name || activeOrg.name
      : user?.organization_name || null
  // Never show the synthetic org workspace address as the account line.
  const footerAccountLine = isSyntheticWorkspaceEmail(displayLabel) ? '' : displayLabel
  const footerTriggerTitle = footerOrgName
    ? [footerOrgName, footerAccountLine].filter(Boolean).join(' — ')
    : footerAccountLine

  const renderAccountTriggerLabel = () => (
    <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0 group-data-[collapsible=icon]:tw-hidden">
      {footerOrgName ? (
        <span className="tw-truncate tw-text-sm tw-font-semibold tw-leading-tight tw-text-slate-900">
          {footerOrgName}
        </span>
      ) : null}
      {footerAccountLine ? (
      <span
        className={cn(
          'tw-truncate tw-leading-tight',
          footerOrgName
            ? 'tw-text-xs tw-font-normal tw-text-slate-500'
            : 'tw-text-sm tw-font-medium tw-text-slate-700'
        )}
      >
        {footerAccountLine}
      </span>
      ) : null}
    </span>
  )

  useEffect(() => {
    setSidebarAvatarError(false)
  }, [resolvedProfileImageUrl])

  /** True when the current pathname belongs to the AI Chat app group (AI Chat itself or its sub-pages like Task Library). */
  const isAiAppPath = (pathname: string) =>
    pathname.startsWith('/ai') || pathname.startsWith('/task-library')

  useEffect(() => {
    if (isMobile) {
      if (isActive('/dashboard')) setOpenSections({ ...SIDEBAR_SECTIONS_ALL_CLOSED, dashboard: true })
      else if (isActive('/traffic')) setOpenSections({ ...SIDEBAR_SECTIONS_ALL_CLOSED, traffic: true })
      else if (isAiAppPath(location.pathname)) setOpenSections({ ...SIDEBAR_SECTIONS_ALL_CLOSED, ai: true })
      else if (isActive('/shipping')) setOpenSections({ ...SIDEBAR_SECTIONS_ALL_CLOSED, shipping: true })
    } else {
      if (isActive('/dashboard')) setOpenSections((prev) => ({ ...prev, dashboard: true }))
      else if (isActive('/traffic')) setOpenSections((prev) => ({ ...prev, traffic: true }))
      else if (isAiAppPath(location.pathname)) setOpenSections((prev) => ({ ...prev, ai: true }))
      else if (isActive('/shipping')) setOpenSections((prev) => ({ ...prev, shipping: true }))
    }
  }, [location.pathname, isMobile])

  const pushSidebarView = (viewId: SidebarViewId) => {
    setNavStack((prev) => (prev[prev.length - 1] === viewId ? prev : [...prev, viewId]))
  }

  const popSidebarView = () => {
    setNavStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))
  }

  useEffect(() => {
    if (sidebarState === 'collapsed') {
      setNavStack(['main'])
      return
    }
    if (isAiAppPath(location.pathname)) {
      setNavStack(['main', 'ai'])
    } else if (location.pathname.startsWith('/plan')) {
      setNavStack(['main', 'plan'])
    } else if (
      location.pathname.startsWith('/settings') &&
      new URLSearchParams(location.search).get('view') === 'shared_accounts'
    ) {
      setNavStack(['main', 'my-team'])
    } else {
      setNavStack(['main'])
    }
  }, [location.pathname, location.search, sidebarState, inOrgContext])

  // Filter chat history for current user (no limit — "Load more" appends via fetchMoreOpenAIChatHistory).
  // Exclude source_page=mcp as a stale-state guard; server already omits them from the list API.
  const filteredChatHistory = _.chain(openAIChatHistory)
    .filter(({ app_email, source_page }) => app_email === user.email && source_page !== 'mcp')
    .uniqBy('message_id')
    .value()

  const sidebarDisplayChats = isSidebarSearching ? chatSearchItems : filteredChatHistory
  const { pinnedSidebarChats, recentSidebarChats } = useMemo(() => {
    if (isSidebarSearching) return { pinnedSidebarChats: [], recentSidebarChats: sidebarDisplayChats }
    return sidebarDisplayChats.reduce(
      (acc: { pinnedSidebarChats: typeof sidebarDisplayChats; recentSidebarChats: typeof sidebarDisplayChats }, c) => {
        if (c.pinned_at) acc.pinnedSidebarChats.push(c)
        else acc.recentSidebarChats.push(c)
        return acc
      },
      { pinnedSidebarChats: [], recentSidebarChats: [] }
    )
  }, [sidebarDisplayChats, isSidebarSearching])

  const isInOrgWorkspace = isWorkspaceContext || !!user?.is_workspace_context
  const showNavItems =
    (subscriptionActive || isLite || upgradeDismissed || status) &&
    // #693: site-tools / no-store users have no linked accounts yet, but still
    // need the AI Chat + Integrations nav (analytics items are hidden below).
    (hasAccounts || isInOrgWorkspace || siteToolsOnly)

  const handleSignOut = () => {
    setAccountPopoverOpen(false)
    if (isMobile) setOpenMobile(false)
    signOutAction().then(() => {
      redirectHomepage('You have signed out', 'info')
    })
  }

  const handleUnMimic = () => {
    const token = getMimicTokenFromCookie()
    if (token) {
      unMimicUserAction(token).then(() => {
        redirectAfterUnmimic('/users')
      })
    }
  }

  const handleNavClick = (nextApp: string, url: string) => {
    // Staff admin SPA is a separate app — never soft-nav into mangosteen /admin chrome.
    if (url.startsWith('/admin')) {
      if (isMobile) setOpenMobile(false)
      window.location.assign(url)
      return
    }
    changeApplication(currentApp, nextApp)
    navigate(url)
    // Update query JSON so Dashboard/Traffic can pick up view changes
    setTimeout(() => updateQueryJsonAction(), 0)
    if (isMobile) setOpenMobile(false)
  }

  const handleChatHistoryClick = (e: React.MouseEvent, chat: any) => {
    e.preventDefault()
    // Clear scheduled-task selection so main AI view shows chat (not task detail)
    dispatch(setSelectedTaskId(null))
    // Navigate to AI chat page with the chat ID as state (plain /ai drops ?view=scheduled-tasks etc.)
    changeApplication(currentApp, 'ai')
    navigate('/ai', { state: { selectedChat: chat } })
    if (isMobile) setOpenMobile(false)
  }

  const handleViewAllChatHistory = (e: React.MouseEvent) => {
    e.preventDefault()
    changeApplication(currentApp, 'ai')
    dispatch(setSelectedTaskId(null))
    navigate('/ai?view=chat-history')
    if (isMobile) setOpenMobile(false)
  }

  const handleDeleteChat = async (e: React.MouseEvent, chatId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (!window.confirm('Delete this chat? This cannot be undone.')) return
    try {
      await dispatch(deleteOpenAIChatHistory({
        message_id: chatId,
        app_email: user.email,
      }))
      toast.success('Chat deleted')
    } catch (error) {
      console.error('Failed to delete chat:', error)
      toast.error('Failed to delete chat')
    }
  }

  const handleDeleteAllChats = async () => {
    if (!window.confirm('Delete all chat history? This cannot be undone.')) return
    try {
      await dispatch(deleteAllOpenAIChatHistory({ app_email: user.email }))
      toast.success('All chat history deleted')
      changeApplication(currentApp, 'ai')
      navigate('/ai', { state: { action: 'new-chat' }, replace: true })
      if (isMobile) setOpenMobile(false)
    } catch (error) {
      console.error('Failed to delete all chats:', error)
      toast.error('Failed to delete all chats')
    }
  }

  const handlePrivacyToggle = async (e: React.MouseEvent, chat: any) => {
    e.preventDefault()
    e.stopPropagation()
    const isPrivate = !!(chat?.is_private ?? false)
    try {
      await dispatch(updateOpenAIChatPrivacy({
        message_id: chat.message_id,
        app_email: user.email,
        is_private: !isPrivate,
      }))
      toast.success(!isPrivate ? 'Chat is now private' : 'Chat shared with team')
    } catch (error) {
      console.error('Failed to update chat privacy:', error)
      toast.error('Failed to update privacy')
    }
  }

  const handlePinToggle = async (e: React.MouseEvent, chat: { message_id: string; pinned_at?: string | null }) => {
    e.preventDefault()
    e.stopPropagation()
    const isPinned = !!chat?.pinned_at
    try {
      await dispatch(togglePinOpenAIChatHistory({
        message_id: chat.message_id,
        app_email: user.email,
        pinned: !isPinned,
      }))
    } catch (error) {
      console.error('Failed to toggle pin:', error)
      toast.error('Failed to update pin')
    }
  }

  const isActive = (url: string) => {
    const urlPath = url.split('?')[0]
    return location.pathname.startsWith(urlPath)
  }

  const isSubItemActive = (url: string) => {
    const [urlPath, urlSearch] = url.split('?')
    const locPath = location.pathname.replace(/\/+$/, '')
    const normalizedPath = (urlPath || '').replace(/\/+$/, '')
    if (normalizedPath.endsWith('/shipping/settings')) {
      return locPath.endsWith('/shipping/settings')
    }
    // Path-based shipping routes (Orders / Shipments). Shipments differentiates
    // its sub-views (transit default / insured / claims) by the `?view=` param.
    if (normalizedPath.endsWith('/shipping/orders')) {
      return locPath.endsWith('/shipping/orders') || locPath.includes('/shipping/orders/')
    }
    if (normalizedPath.endsWith('/shipping/shipments')) {
      const onShipments =
        locPath.endsWith('/shipping/shipments') || locPath.includes('/shipping/shipments/')
      if (!onShipments) return false
      const targetView = new URLSearchParams(urlSearch || '').get('view') || ''
      const currentView = new URLSearchParams(location.search).get('view') || ''
      return targetView === currentView
    }
    if (locPath !== normalizedPath) return false
    if (!urlSearch) return false
    const searchParams = new URLSearchParams(location.search)
    const urlParams = new URLSearchParams(urlSearch)
    // Check for 'view' param (Dashboard, Traffic) or 'tab' param (Shipping)
    if (urlParams.has('view')) {
      return searchParams.get('view') === urlParams.get('view')
    }
    // Check for 'tab' param (Shipping) - only if it exists in target URL
    if (urlParams.has('tab')) {
      return searchParams.get('tab') === urlParams.get('tab')
    }
    return false
  }

  const dismissAccountMenu = () => {
    setAccountPopoverOpen(false)
  }

  const handleAccountPopoverOpenChange = (open: boolean) => {
    setAccountPopoverOpen(open)
    if (open && organizationsStatus === 'failed') {
      dispatch(fetchOrganizations() as never)
    }
  }

  const openCreateOrgWizard = async () => {
    setAccountPopoverOpen(false)
    const result = await dispatch(provisionOrganizationTenancy() as never)
    if (provisionOrganizationTenancy.rejected.match(result)) {
      toast.error(
        (result.payload as string) || 'Could not prepare organization workspace. Try again.'
      )
      return
    }
    setCreateOrgWizardOpen(true)
  }

  const renderDrillDownCaret = () => (
    <ChevronRight className="tw-ml-auto tw-h-4 tw-w-4 tw-text-slate-400" />
  )

  const renderAccountPopoverMenu = () => (
    <div className="tw-py-1">
      <OrgSwitcherMenu
        onDismiss={dismissAccountMenu}
        onOpenCreateWizard={openCreateOrgWizard}
        isAccountOwner={isOwner}
      />
      <button
        type="button"
        className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-100 tw-transition-colors"
        onClick={() => {
          dismissAccountMenu()
          navigate('/profile')
          if (isMobile) setOpenMobile(false)
        }}
      >
        <UserCircle className="tw-h-4 tw-w-4 tw-flex-shrink-0" />
        <span className="tw-truncate">Profile</span>
      </button>
      {planNavAllowed ? (
        <a
          href="/plan"
          className={cn(
            'tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-100 tw-no-underline tw-transition-colors',
            isActive('/plan') && 'tw-font-semibold'
          )}
          onClick={(e) => {
            e.preventDefault()
            dismissAccountMenu()
            if (sidebarState === 'collapsed') setOpen(true)
            handleNavClick('plan', '/plan')
            // Already on /plan: navigate() is a no-op, so pathname effect won't re-run after Back.
            if (sidebarState !== 'collapsed') {
              setNavStack(['main', 'plan'])
            }
          }}
        >
          <CreditCard className="tw-h-4 tw-w-4" />
          <span className="tw-flex-1">My Plan</span>
          {sidebarState !== 'collapsed' ? renderDrillDownCaret() : null}
        </a>
      ) : null}
      {visibleAccountNavItems.map((item) => {
        const appId = item.url.split('?')[0].replace('/', '')
        const isMyTeam = item.permissionKey === 'shared_accounts'
        const usesDrillDown = isMyTeam
        const itemActive = item.external
          ? false
          : item.url.includes('?')
            ? isSubItemActive(item.url)
            : isActive(item.url)
        return (
          <a
            key={item.title}
            href={item.url}
            className={cn(
              'tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 hover:tw-bg-slate-100 tw-no-underline tw-transition-colors',
              itemActive && 'tw-font-semibold'
            )}
            target={item.external ? '_blank' : undefined}
            rel={item.external ? 'noopener noreferrer' : undefined}
            onClick={(e) => {
              if (item.external) {
                dismissAccountMenu()
                return
              }
              e.preventDefault()
              dismissAccountMenu()
              if (usesDrillDown) {
                if (sidebarState === 'collapsed') setOpen(true)
                handleNavClick(appId, item.url)
                if (sidebarState !== 'collapsed') {
                  setNavStack(['main', 'my-team'])
                }
              } else {
                handleNavClick(appId, item.url)
              }
            }}
          >
            <item.icon className="tw-h-4 tw-w-4" />
            <span className="tw-flex-1">{item.title}</span>
            {usesDrillDown && sidebarState !== 'collapsed' ? renderDrillDownCaret() : null}
          </a>
        )
      })}

      {status && (
        <>
          <div className="tw-my-1 tw-h-px tw-bg-slate-200" />
          <button
            type="button"
            className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-transition-colors"
            onClick={() => {
              dismissAccountMenu()
              if (isMobile) setOpenMobile(false)
              handleUnMimic()
            }}
          >
            <Ban className="tw-h-4 tw-w-4" />
            End Impersonation
          </button>
        </>
      )}
      <div className="tw-my-1 tw-h-px tw-bg-slate-200" />
      <button
        type="button"
        className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-transition-colors"
        onClick={() => {
          handleSignOut()
        }}
      >
        <LogOut className="tw-h-4 tw-w-4" />
        Log Out
      </button>
    </div>
  )

  const currentSidebarView = navStack[navStack.length - 1]
  const isRootSidebarView = currentSidebarView === 'main'

  const changelogEmbedUrl = useMemo(() => {
    const id = user?.id
    const email = user?.email
    if (id == null && !email) return null
    const params = new URLSearchParams()
    if (id != null) params.set('userId', String(id))
    if (email) params.set('email', email)
    return `https://www.payhelm.com/changelog-embed?${params.toString()}`
  }, [user?.id, user?.email])

  /** null = waiting for embed postMessage; true = has entries; false = hide block */
  const [changelogHasContent, setChangelogHasContent] = useState<boolean | null>(null)

  useEffect(() => {
    if (!changelogEmbedUrl || sidebarState === 'collapsed' || !isRootSidebarView) {
      setChangelogHasContent(null)
      return
    }
    setChangelogHasContent(null)

    const isChangelogEmbedOrigin = (origin: string) => {
      try {
        const { hostname } = new URL(origin)
        return hostname === 'payhelm.com' || hostname.endsWith('.payhelm.com')
      } catch {
        return false
      }
    }

    let timeoutId: ReturnType<typeof window.setTimeout>
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'payhelm:changelog' || !isChangelogEmbedOrigin(event.origin)) return
      const newCount = typeof event.data.newCount === 'number' ? event.data.newCount : 0
      const newEntries = Array.isArray(event.data.newEntries) ? event.data.newEntries : []
      setChangelogHasContent(newCount > 0 || newEntries.length > 0)
      window.clearTimeout(timeoutId)
    }

    window.addEventListener('message', onMessage)
    timeoutId = window.setTimeout(() => {
      setChangelogHasContent((current) => (current === null ? false : current))
    }, 10_000)

    return () => {
      window.removeEventListener('message', onMessage)
      window.clearTimeout(timeoutId)
    }
  }, [changelogEmbedUrl, sidebarState, isRootSidebarView])

  const handleAiStaticItemClick = (e: React.MouseEvent, action: string | undefined, url: string) => {
    e.preventDefault()
    if (action === 'new-chat') {
      changeApplication(currentApp, 'ai')
      dispatch(setSelectedTaskId(null))
      navigate('/ai', { state: { action: 'new-chat' } })
      if (isMobile) setOpenMobile(false)
      return
    }
    if (action === 'custom-tools') {
      changeApplication(currentApp, 'ai')
      dispatch(setSelectedTaskId(null))
      navigate('/ai?view=custom-tools')
      if (isMobile) setOpenMobile(false)
      return
    }
    if (action === 'tool-access') {
      changeApplication(currentApp, 'ai')
      dispatch(setSelectedTaskId(null))
      navigate('/ai?view=tool-access')
      if (isMobile) setOpenMobile(false)
      return
    }
    if (action === 'task-library') {
      // Task Library lives under AI Chat. Keep the AI app context so the AI drill-down stays open.
      changeApplication(currentApp, 'ai')
      dispatch(setSelectedTaskId(null))
      navigate('/task-library')
      if (isMobile) setOpenMobile(false)
      return
    }
    handleNavClick('ai', url)
  }

  const renderAiMenuContent = () => {
    const aiSearch = new URLSearchParams(location.search)
    const aiView = aiSearch.get('view')
    const isCustomToolsView = location.pathname === '/ai' && aiView === 'custom-tools'
    const isScheduledTasksSidebarView = location.pathname === '/ai' && aiView === 'scheduled-tasks'
    const isChatHistorySidebarView = location.pathname === '/ai' && aiView === 'chat-history'
    const isToolAccessView = location.pathname === '/ai' && aiView === 'tool-access'
    const isTaskLibraryView = location.pathname.startsWith('/task-library')
    const isNewChatView =
      location.pathname === '/ai' && !isCustomToolsView && !isScheduledTasksSidebarView && !isChatHistorySidebarView && !isToolAccessView

    const newChatSidebarItem = aiChatStaticItems.find((item) => item.action === 'new-chat')
    const NewChatIcon = newChatSidebarItem?.icon
    const otherAiStaticItems = aiChatStaticItems.filter((item) => item.action !== 'new-chat')

    return (
    <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
      {/* Pinned: New Chat, then Clear history, then Custom Tools */}
      <SidebarMenu className="tw-shrink-0">
        {newChatSidebarItem && NewChatIcon && (
          <SidebarMenuItem key={newChatSidebarItem.title}>
            <SidebarMenuButton asChild isActive={isNewChatView} className={isNewChatView ? 'tw-font-semibold' : undefined}>
              <a
                href={newChatSidebarItem.url}
                className="tw-no-underline hover:tw-no-underline tw-text-slate-700"
                onClick={(e) => handleAiStaticItemClick(e, newChatSidebarItem.action, newChatSidebarItem.url)}
              >
                <NewChatIcon />
                <span>{newChatSidebarItem.title}</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}


        {otherAiStaticItems.map((item) => {
          const active =
            item.action === 'custom-tools'
              ? isCustomToolsView
              : item.action === 'task-library'
                ? isTaskLibraryView
                : item.action === 'tool-access'
                  ? isToolAccessView
                  : false
          return (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild isActive={active} className={active ? 'tw-font-semibold' : undefined}>
                <a
                  href={item.url}
                  className="tw-no-underline hover:tw-no-underline tw-text-slate-700"
                  onClick={(e) => handleAiStaticItemClick(e, item.action, item.url)}
                >
                  <item.icon />
                  <span>{item.title}</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>

      {/* Recent chats: header fixed; list scrolls */}
      <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col">
        <div className="tw-shrink-0 tw-border-t tw-border-sidebar-border tw-mt-2" />
        <div className="tw-shrink-0 tw-px-2 tw-py-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-text-xs tw-font-semibold tw-text-slate-500">
            Chats
          </span>
          <a
            href="/ai?view=chat-history"
            onClick={handleViewAllChatHistory}
            className={cn(
              'tw-text-[11px] tw-font-medium tw-text-slate-400 hover:tw-text-slate-700 hover:tw-underline tw-no-underline',
              isChatHistorySidebarView && 'tw-text-slate-700'
            )}
          >
            View all
          </a>
        </div>
        {/* Search */}
        <div className="tw-shrink-0 tw-px-2 tw-pb-1">
          <div className="tw-relative">
            <span className="fa fa-search tw-absolute tw-left-2 tw-top-1/2 -tw-translate-y-1/2 tw-text-[10px] tw-text-slate-400 tw-pointer-events-none" />
            <input
              type="search"
              value={chatSearchInput}
              onChange={(e) => setChatSearchInput(e.target.value)}
              placeholder="Search chats..."
              className="tw-h-7 tw-w-full tw-rounded-md tw-border tw-border-slate-200 tw-bg-slate-50 tw-pl-6 tw-pr-6 tw-text-xs tw-text-slate-800 tw-outline-none focus:tw-border-blue-300 focus:tw-bg-white focus:tw-ring-1 focus:tw-ring-blue-100"
            />
            {chatSearchInput && (
              <button
                type="button"
                onClick={() => setChatSearchInput('')}
                className="tw-absolute tw-right-1.5 tw-top-1/2 -tw-translate-y-1/2 tw-rounded tw-border-0 tw-bg-transparent tw-p-0.5 tw-text-slate-400 hover:tw-text-slate-600"
              >
                <span className="fa fa-times tw-text-[10px]" />
              </button>
            )}
          </div>
        </div>
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-overflow-x-hidden hidden-scrollbar">
          <SidebarMenu>
            {chatSearchLoading && (
              <div className="tw-px-4 tw-py-2 tw-text-xs tw-text-slate-400 tw-text-center">
                <span className="fa fa-spinner fa-spin tw-mr-1" />
                Searching...
              </div>
            )}
            {!chatSearchLoading && pinnedSidebarChats.length > 0 && (
              <>
                <div className="tw-px-2 tw-pt-1 tw-pb-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-400">
                  Pinned
                </div>
                {pinnedSidebarChats.map((chat) => {
                  const chatMessage = getChatHistoryListPreview(chat)
                  const isChatLoading = activeChatIds.includes(chat.message_id)
                  return (
                    <SidebarMenuItem key={chat.message_id}>
                      <SidebarMenuButton asChild>
                        <a
                          href="/ai"
                          className="tw-no-underline hover:tw-no-underline tw-text-slate-700 tw-group/chat"
                          onClick={(e) => handleChatHistoryClick(e, chat)}
                          title={chatMessage}
                        >
                          {isChatLoading && (
                            <span className="fa fa-spinner fa-spin tw-text-xs tw-text-slate-400 tw-mr-1 tw-shrink-0" title="In progress" />
                          )}
                          <span className="tw-truncate tw-flex-1 tw-text-xs">
                            {chatMessage.substring(0, 30)}{chatMessage.length > 30 ? '...' : ''}
                          </span>
                          <span className="tw-relative tw-shrink-0 tw-flex tw-items-center tw-gap-0.5">
                            <button
                              className="tw-p-1 tw-rounded tw-text-blue-400 hover:tw-text-blue-600 tw-ml-0.5"
                              onClick={(e) => handlePinToggle(e, chat)}
                              title="Unpin"
                            >
                              <span className="fa fa-thumb-tack tw-text-xs" />
                            </button>
                          </span>
                        </a>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
                {recentSidebarChats.length > 0 && (
                  <div className="tw-px-2 tw-pt-2 tw-pb-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-400">
                    Recent
                  </div>
                )}
              </>
            )}
            {!chatSearchLoading && recentSidebarChats.map((chat) => {
              const chatMessage = getChatHistoryListPreview(chat)
              const isChatLoading = activeChatIds.includes(chat.message_id)

              return (
                <SidebarMenuItem key={chat.message_id}>
                  <SidebarMenuButton asChild>
                    <a
                      href="/ai"
                      className="tw-no-underline hover:tw-no-underline tw-text-slate-700 tw-group/chat"
                      onClick={(e) => handleChatHistoryClick(e, chat)}
                      title={chatMessage}
                    >
                      {isChatLoading && (
                        <span
                          className="fa fa-spinner fa-spin tw-text-xs tw-text-slate-400 tw-mr-1 tw-shrink-0"
                          title="In progress"
                        />
                      )}
                      <span className="tw-truncate tw-flex-1 tw-text-xs">
                        {chatMessage.substring(0, 30)}{chatMessage.length > 30 ? '...' : ''}
                      </span>
                      {/* Action buttons + badges share one slot.
                          Buttons are opacity-0 at rest (always in DOM → tappable on touch).
                          Badges absolutely overlay the button area at rest and fade on hover. */}
                      <span className="tw-relative tw-shrink-0 tw-flex tw-items-center tw-gap-0.5">
                        <button
                          className="tw-opacity-0 group-hover/chat:tw-opacity-100 tw-p-1 tw-rounded hover:tw-bg-slate-100 tw-text-slate-400 hover:tw-text-blue-500 tw-transition-opacity tw-ml-0.5"
                          onClick={(e) => handlePinToggle(e, chat)}
                          title="Pin"
                        >
                          <span className="fa fa-thumb-tack tw-text-xs" />
                        </button>
                        <button
                          className="tw-opacity-0 group-hover/chat:tw-opacity-100 tw-p-1 tw-rounded hover:tw-bg-slate-100 tw-text-slate-400 hover:tw-text-slate-600 tw-transition-opacity tw-ml-0.5"
                          onClick={(e) => handlePrivacyToggle(e, chat)}
                          title={chat?.is_private ? 'Private – click to share with team' : 'Shared – click to make private'}
                        >
                          <span className={`fa ${chat?.is_private ? 'fa-eye-slash' : 'fa-eye'} tw-text-xs`} />
                        </button>
                        <button
                          className="tw-opacity-0 group-hover/chat:tw-opacity-100 tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-slate-400 hover:tw-text-red-600 tw-transition-opacity tw-ml-0.5"
                          onClick={(e) => handleDeleteChat(e, chat.message_id)}
                          title="Delete chat"
                        >
                          <span className="fa fa-trash tw-text-xs" />
                        </button>
                        {(() => {
                          const s = toolAccessSummary[chat.message_id]
                          if (!s || s.total === 0) return null
                          return (
                            <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-end tw-gap-0.5 tw-pointer-events-none group-hover/chat:tw-opacity-0 tw-transition-opacity">
                              {s.failed > 0 && (
                                <span className="tw-text-[9px] tw-font-semibold tw-px-1 tw-py-0.5 tw-rounded tw-bg-red-100 tw-text-red-600" title={`${s.failed} failed`}>
                                  {s.failed}✗
                                </span>
                              )}
                              {s.writes > 0 && (
                                <span className="tw-text-[9px] tw-font-semibold tw-px-1 tw-py-0.5 tw-rounded tw-bg-amber-100 tw-text-amber-700" title={`${s.writes} write actions`}>
                                  {s.writes}W
                                </span>
                              )}
                              <span className="tw-text-[9px] tw-px-1 tw-py-0.5 tw-rounded tw-bg-slate-100 tw-text-slate-500" title={`${s.total} tool calls`}>
                                {s.total}
                              </span>
                            </span>
                          )
                        })()}
                      </span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}

            {!isSidebarSearching && recentSidebarChats.length > 0 && chatHistoryHasMore && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleLoadMoreHistory}
                  disabled={chatHistoryLoadingMore}
                  className="tw-w-full tw-px-4 tw-py-2 tw-text-xs tw-text-slate-400 hover:tw-text-slate-600 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1"
                >
                  {chatHistoryLoadingMore ? (
                    <span className="fa fa-spinner fa-spin tw-text-xs" />
                  ) : (
                    <span className="fa fa-chevron-down tw-text-xs" />
                  )}
                  Load more
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {!chatSearchLoading && sidebarDisplayChats.length === 0 && (
              <div className="tw-px-4 tw-py-2 tw-text-xs tw-text-slate-500 tw-text-center">
                {isSidebarSearching ? `No results for "${trimmedChatSearch}"` : 'No chat history yet'}
              </div>
            )}
          </SidebarMenu>
        </div>
      </div>
    </div>
    )
  }

  const renderPlanMenuContent = () => {
    const items = visiblePlanNavItems(billingChannel)

    return (
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
        <SidebarMenu>
          {items.map((item) => {
            const active = isSubItemActive(item.url)
            const Icon = item.icon
            return (
              <SidebarMenuItem key={item.viewKey}>
                <SidebarMenuButton asChild isActive={active} className={active ? 'tw-font-semibold' : undefined}>
                  <a
                    href={item.url}
                    className="tw-no-underline hover:tw-no-underline tw-text-slate-700"
                    onClick={(e) => {
                      e.preventDefault()
                      handleNavClick('plan', item.url)
                    }}
                  >
                    <Icon />
                    <span>{item.title}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </div>
    )
  }

  const renderMyTeamMenuContent = () => {
    const teamTabItems = [
      { title: 'Members', url: '/settings?view=shared_accounts&tab=members', icon: Users },
      { title: 'My AI', url: '/settings?view=shared_accounts&tab=my-ai', icon: Sparkles },
    ]
    const currentParams = new URLSearchParams(location.search)
    return (
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-h-0">
        <SidebarMenu>
          {teamTabItems.map((item) => {
            const itemParams = new URLSearchParams(item.url.split('?')[1] || '')
            const active =
              location.pathname.startsWith('/settings') &&
              currentParams.get('view') === itemParams.get('view') &&
              currentParams.get('tab') === itemParams.get('tab')
            const Icon = item.icon
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild isActive={active} className={active ? 'tw-font-semibold' : undefined}>
                  <a
                    href={item.url}
                    className="tw-no-underline hover:tw-no-underline tw-text-slate-700"
                    onClick={(e) => {
                      e.preventDefault()
                      handleNavClick('settings', item.url)
                    }}
                  >
                    <Icon />
                    <span>{item.title}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </div>
    )
  }


  const drillDownViews: Record<Exclude<SidebarViewId, 'main'>, { title: string; render: () => React.ReactNode }> = {
    ai: {
      title: 'AI Chat',
      render: renderAiMenuContent,
    },
    plan: {
      title: 'My Plan',
      render: renderPlanMenuContent,
    },
    'my-team': {
      title: 'My Team',
      render: renderMyTeamMenuContent,
    },
  }

  const activeDrillDownView = isRootSidebarView
    ? null
    : drillDownViews[currentSidebarView as Exclude<SidebarViewId, 'main'>]

  return (
    <>
    <Sidebar collapsible="icon">
      <SidebarHeader
        className={cn(
          'tw-items-start',
          sidebarShowsIconLogo && 'tw-h-12 tw-min-h-12 tw-pt-0 tw-pb-2 tw-flex tw-flex-row tw-items-center'
        )}
      >
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="tw-justify-start">
              <a
                href="/dashboard"
                className={cn(
                  'tw-flex tw-items-center tw-justify-center tw-no-underline hover:tw-no-underline',
                  sidebarShowsIconLogo && 'tw-h-12 tw-w-full tw-flex tw-items-center tw-justify-center'
                )}
                onClick={(e) => {
                  e.preventDefault()
                  // #693: home is /dashboard for everyone. For site-tools/no-store
                  // users it renders the SiteToolsLanding connect hub (not empty
                  // analytics), so keep the logo consistent with defaultRedirect/finish().
                  navigate('/dashboard')
                  if (isMobile) setOpenMobile(false)
                }}
              >
                <img
                  src={sidebarShowsIconLogo ? IconLogo : Logo}
                  alt="PayHelm"
                  className={cn(sidebarShowsIconLogo ? 'tw-h-7 tw-w-7 tw-object-contain' : 'tw-h-8')}
                />
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className={cn('tw-flex tw-flex-col', isMobile && 'tw-min-h-0 tw-overflow-y-auto')}>
        <div className={cn('tw-relative tw-flex-1 tw-min-h-0', isMobile ? 'tw-overflow-x-hidden tw-overflow-y-visible' : 'tw-overflow-x-hidden')}>
          <div
            className={cn(
              'tw-transition-all tw-duration-200 tw-ease-out',
              isRootSidebarView
                ? 'tw-opacity-100 tw-translate-x-0'
                : 'tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-0 tw--translate-x-2'
            )}
          >
            {showNavItems && (
              <SidebarGroup>
                <SidebarGroupLabel>Navigation</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {/* Dashboard is always available after onboard (seeded custom tabs +
                        connect CTAs for no-store). Traffic stays store-data-gated. */}
                    <Collapsible
                      open={isMobile ? openSections.dashboard : isActive('/dashboard') || openSections.dashboard}
                      onOpenChange={isMobile ? setSectionOpen('dashboard') : (open) => setOpenSections((prev) => ({ ...prev, dashboard: open }))}
                      className="tw-group/collapsible tw-w-full"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            tooltip="Dashboard"
                            isActive={isActive('/dashboard')}
                            className={isActive('/dashboard') ? 'tw-font-semibold' : undefined}
                            onClick={(e) => {
                              if (sidebarState === 'collapsed') {
                                e.preventDefault()
                                e.stopPropagation()
                                setOpen(true)
                                setOpenSections({ ...SIDEBAR_SECTIONS_ALL_CLOSED, dashboard: true })
                                handleNavClick('dashboard', dashboardItems[0].url)
                              }
                            }}
                          >
                            <LayoutDashboard />
                            <span className={isActive('/dashboard') ? 'tw-font-semibold' : undefined}>Dashboard</span>
                            <ChevronDown className="tw-ml-auto tw-transition-transform group-data-[state=open]/collapsible:tw-rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {visibleDashboardItems.map((item) => (
                              <SidebarMenuSubItem key={item.url}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isSubItemActive(item.url)}
                                >
                                  <a
                                    href={item.url}
                                    className={`tw-no-underline hover:tw-no-underline tw-text-slate-700 ${
                                      isSubItemActive(item.url) ? 'tw-font-semibold' : ''
                                    }`}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleNavClick('dashboard', item.url)
                                    }}
                                  >
                                    <item.icon />
                                    <span>{item.title}</span>
                                  </a>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>

                    {!siteToolsOnly && (
                    <>
                    {/* Traffic with submenu */}
                    <Collapsible
                      open={isMobile ? openSections.traffic : isActive('/traffic') || openSections.traffic}
                      onOpenChange={isMobile ? setSectionOpen('traffic') : (open) => setOpenSections((prev) => ({ ...prev, traffic: open }))}
                      className="tw-group/collapsible tw-w-full"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            tooltip="Traffic"
                            isActive={isActive('/traffic')}
                            className={isActive('/traffic') ? 'tw-font-semibold' : undefined}
                            onClick={(e) => {
                              if (sidebarState === 'collapsed') {
                                e.preventDefault()
                                e.stopPropagation()
                                setOpen(true)
                                setOpenSections({ ...SIDEBAR_SECTIONS_ALL_CLOSED, traffic: true })
                                handleNavClick('traffic', trafficItems[0].url)
                              }
                            }}
                          >
                            <TrendingUp />
                            <span className={isActive('/traffic') ? 'tw-font-semibold' : undefined}>Traffic</span>
                            <ChevronDown className="tw-ml-auto tw-transition-transform group-data-[state=open]/collapsible:tw-rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {trafficItems.map((item) => (
                              <SidebarMenuSubItem key={item.title}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isSubItemActive(item.url)}
                                >
                                  <a
                                    href={item.url}
                                    className={`tw-no-underline hover:tw-no-underline tw-text-slate-700 ${
                                      isSubItemActive(item.url) ? 'tw-font-semibold' : ''
                                    }`}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleNavClick('traffic', item.url)
                                    }}
                                  >
                                    <item.icon />
                                    <span>{item.title}</span>
                                  </a>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                    </>
                    )}

                    {/* AI Chat drill-down */}
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip="AI Chat"
                        isActive={isAiAppPath(location.pathname)}
                        className={isAiAppPath(location.pathname) ? 'tw-font-semibold' : undefined}
                        onClick={() => {
                          if (sidebarState === 'collapsed') {
                            setOpen(true)
                            handleNavClick('ai', '/ai')
                            return
                          }
                          if (isMobile) {
                            if (!isAiAppPath(location.pathname)) {
                              // Not on AI page: open new chat, keep sidebar open
                              changeApplication(currentApp, 'ai')
                              navigate('/ai', { state: { action: 'new-chat' } })
                            } else {
                              // Already on AI page: show AI sidebar (New Chat, Settings, recent chats)
                              pushSidebarView('ai')
                            }
                          } else {
                            pushSidebarView('ai')
                            // Only navigate to /ai when not already on an AI sub-page
                            if (!isAiAppPath(location.pathname)) {
                              handleNavClick('ai', '/ai')
                            }
                          }
                        }}
                      >
                        <Sparkles />
                        <span className={isAiAppPath(location.pathname) ? 'tw-font-semibold' : undefined}>AI Chat</span>
                        {renderDrillDownCaret()}
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {/* Other nav items */}
                    {visibleMainNavItems.map((item) => {
                      const appId = item.url.split('?')[0].replace('/', '')
                      return (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton
                            tooltip={item.title}
                            asChild
                            isActive={isActive(item.url)}
                            className={isActive(item.url) ? 'tw-font-semibold' : undefined}
                          >
                            <a
                              href={item.url}
                              className="tw-no-underline hover:tw-no-underline tw-text-slate-700"
                              onClick={(e) => {
                                e.preventDefault()
                                if (sidebarState === 'collapsed') setOpen(true)
                                handleNavClick(appId, item.url)
                              }}
                            >
                              <item.icon />
                              <span className={isActive(item.url) ? 'tw-font-semibold' : undefined}>
                                {item.title}
                              </span>
                            </a>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )
                    })}

                    {/* Shipping with submenu — hidden when the shipping release flag is off,
                        or for store-less site-tools-only accounts (no orders to ship). */}
                    {shippingFeatureEnabled && !siteToolsOnly && (
                    <Collapsible
                      open={isMobile ? openSections.shipping : isActive('/shipping') || openSections.shipping}
                      onOpenChange={isMobile ? setSectionOpen('shipping') : (open) => setOpenSections((prev) => ({ ...prev, shipping: open }))}
                      className="tw-group/collapsible tw-w-full"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            tooltip="Shipping"
                            isActive={isActive('/shipping')}
                            className={isActive('/shipping') ? 'tw-font-semibold' : undefined}
                            onClick={(e) => {
                              if (sidebarState === 'collapsed') {
                                e.preventDefault()
                                e.stopPropagation()
                                setOpen(true)
                                setOpenSections({ ...SIDEBAR_SECTIONS_ALL_CLOSED, shipping: true })
                                handleNavClick('shipping', shippingItems[0].url)
                              }
                            }}
                          >
                            <Truck />
                            <span className={isActive('/shipping') ? 'tw-font-semibold' : undefined}>Shipping</span>
                            <ChevronDown className="tw-ml-auto tw-transition-transform group-data-[state=open]/collapsible:tw-rotate-180" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {shippingItems.map((item) => (
                              <SidebarMenuSubItem key={item.title}>
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isSubItemActive(item.url)}
                                >
                                  <a
                                    href={item.url}
                                    className={`tw-no-underline hover:tw-no-underline tw-text-slate-700 ${
                                      isSubItemActive(item.url) ? 'tw-font-semibold' : ''
                                    }`}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      handleNavClick('shipping', item.url)
                                    }}
                                  >
                                    <item.icon />
                                    <span>{item.title}</span>
                                  </a>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}



            {/* Sidebar promo card */}
            {/* {sidebarState !== 'collapsed' && (
              <div className="tw-p-4">
                <PromoCard
                  title={sidebarPromoCard.title}
                  description={sidebarPromoCard.description}
                  buttonLabel={sidebarPromoCard.buttonLabel}
                  imageUrl={sidebarPromoCard.imageUrl}
                  storageKey="payhelm.sidebarPromoCard.dismissed"
                  onActionClick={() => handleNavClick('plan', '/plan?view=options')}
                />
              </div>
            )} */}
          </div>

          <div
            className={cn(
              'tw-transition-all tw-duration-200 tw-ease-out',
              !isRootSidebarView
                ? 'tw-opacity-100 tw-translate-x-0 tw-flex tw-flex-col tw-h-full'
                : 'tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-0 tw-translate-x-2'
            )}
          >
            <SidebarDrillDownPanel title={activeDrillDownView?.title ?? ''} onBack={popSidebarView}>
              {activeDrillDownView?.render()}
            </SidebarDrillDownPanel>
          </div>
        </div>

        <div className="tw-mt-auto">
          <div className="tw-px-2 tw-pb-2">
            <BackfillBanner collapsed={!isMobile && sidebarState === 'collapsed'} isMobile={isMobile} />
          </div>
        </div>

        {changelogEmbedUrl &&
          changelogHasContent !== false &&
          sidebarState !== 'collapsed' &&
          isRootSidebarView && (
            <div
              className={cn(
                'tw-px-2 tw-pb-2',
                changelogHasContent !== true && 'tw-hidden'
              )}
            >
              <iframe
                src={changelogEmbedUrl}
                className="tw-h-[260px] tw-w-full tw-border-0 tw-block"
                title="What's New — PayHelm"
              />
            </div>
          )}
      </SidebarContent>

      <SidebarFooter
        className={cn(
          'tw-border-t tw-border-sidebar-border tw-p-2',
          isMobile && 'tw-shrink-0 tw-bg-sidebar tw-pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]'
        )}
      >
        <SidebarMenu>
          <SidebarMenuItem>
            {isMobile ? (
              /* On mobile the sidebar is a Radix Sheet (dialog). Opening a Popover inside a Sheet
                 causes a DismissableLayer conflict: the portal'd popover content triggers the
                 Sheet's outside-click handler and the menu flashes closed immediately.
                 Using an inline Collapsible avoids any floating-layer conflict.
                 flex-col-reverse on Root: trigger stays visually at bottom, menu expands upward (like desktop side="top"). */
              <Collapsible
                open={accountPopoverOpen}
                onOpenChange={handleAccountPopoverOpenChange}
                className="tw-flex tw-w-full tw-flex-col-reverse"
              >
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    type="button"
                    size="lg"
                    className="tw-h-auto tw-p-2 tw-w-full tw-text-left"
                  >
                    <span className="tw-relative tw-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-slate-100 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-700">
                      {profileImageUrl ? (
                        <img
                          src={profileImageUrl}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-center"
                          onError={() => setSidebarAvatarError(true)}
                        />
                      ) : (
                        displayInitial
                      )}
                    </span>
                    {renderAccountTriggerLabel()}
                    <ChevronDown className="tw-h-4 tw-w-4 tw-text-slate-500 tw-flex-shrink-0" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {renderAccountPopoverMenu()}
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <Popover open={accountPopoverOpen} onOpenChange={handleAccountPopoverOpenChange}>
                <PopoverTrigger asChild>
                  <SidebarMenuButton
                    type="button"
                    size="lg"
                    title={sidebarState === 'collapsed' ? footerTriggerTitle : undefined}
                    className="tw-h-auto tw-p-2 tw-w-full tw-text-left group-data-[collapsible=icon]:tw-!size-10 group-data-[collapsible=icon]:tw-!min-w-10 group-data-[collapsible=icon]:tw-!p-0 group-data-[collapsible=icon]:tw-flex group-data-[collapsible=icon]:tw-justify-center group-data-[collapsible=icon]:tw-items-center"
                  >
                    <span className="tw-relative tw-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-slate-100 tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-700 group-data-[collapsible=icon]:tw-h-8 group-data-[collapsible=icon]:tw-w-8">
                      {profileImageUrl ? (
                        <img
                          src={profileImageUrl}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-center"
                          onError={() => setSidebarAvatarError(true)}
                        />
                      ) : (
                        displayInitial
                      )}
                    </span>
                    {renderAccountTriggerLabel()}
                    <ChevronDown className="tw-h-4 tw-w-4 tw-text-slate-500 tw-flex-shrink-0 group-data-[collapsible=icon]:tw-hidden" />
                  </SidebarMenuButton>
                </PopoverTrigger>
                <PopoverContent side="top" align="start" className="tw-w-80 tw-max-h-[min(70vh,32rem)] tw-overflow-y-auto tw-p-0">
                  {renderAccountPopoverMenu()}
                </PopoverContent>
              </Popover>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
    <CreateOrganizationWizard open={createOrgWizardOpen} onOpenChange={setCreateOrgWizardOpen} />
    </>
  )
}

const mapStateToProps = (store: any) => ({
  currentApp: store.root.applicationId,
  isOwner: store.root.isOwner,
  mimicStatus: store.mimic.mimic.status,
  subscriptionActive: store.plan.subscriptionActive,
  upgradeDismissed: store.plan.upgradeDismissed,
  billingChannel: store.plan.billingChannel ?? null,
  // #693 / onboard presets: trim Traffic/Reports/etc. for ANY user without a
  // connected store. Dashboard stays in the nav (seeded custom tabs + connect CTAs).
  // Org / workspace-context users are excluded — they run on workspace data.
  siteToolsOnly:
    !hasConnectedStore(store.root.accounts) &&
    !store.root.user?.is_organization_workspace &&
    !store.root.user?.is_workspace_context,
  sessionSignature: store.root.sessionSignature,
  user: store.root.user,
  profile: selectProfile(store),
  profileStatus: selectProfileStatus(store),
})

const mapDispatchToProps = {
  changeApplication,
  signOut,
  unMimicUser,
  getMimicStatus,
  updateQueryJson,
}

export default connect(mapStateToProps, mapDispatchToProps)(AppSidebarComponent)
